import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { IListCheckEntry, IListCheckRulings } from 'src/ts/interfaces/listCheck';

export type ListCheckStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ListCheckRulingsState {
  status: ListCheckStatus;
  data: IListCheckRulings | null;
  error: string | null;
  retry: () => void;
  applyEntry: (proofId: string, entry: IListCheckEntry) => void;
}

type ProofResult = Omit<ListCheckRulingsState, 'retry' | 'applyEntry'>;

const IDLE: ProofResult = { status: 'idle', data: null, error: null };
const LOADING: ProofResult = { status: 'loading', data: null, error: null };
const FAILED: ProofResult = { status: 'error', data: null, error: 'List checks failed to load' };

export const useListCheckRulings = (
  currentProofId: string | undefined,
  enabled: boolean
): ListCheckRulingsState => {
  const requestedRef = useRef<Set<string>>(new Set());
  const cancelSourceRef = useRef(axios.CancelToken.source());
  const [results, setResults] = useState<Record<string, ProofResult>>({});
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const cancelSource = cancelSourceRef.current;
    return () => {
      cancelSource.cancel();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !currentProofId) return;

    const requested = requestedRef.current;
    if (requested.has(currentProofId)) return;

    const proofId = currentProofId;
    requested.add(proofId);
    setResults((prev) => ({ ...prev, [proofId]: LOADING }));

    const fetchRulings = async () => {
      let outcome: ProofResult;
      try {
        const body = (await defaultInstance.get(
          `${configVars.server_url}/v2/proofs/${proofId}/list_check_rulings`,
          { cancelToken: cancelSourceRef.current.token }
        )) as unknown as IListCheckRulings;
        outcome = { status: 'success', data: body, error: null };
      } catch (error) {
        if (axios.isCancel(error)) return;
        outcome = FAILED;
      }
      setResults((prev) => ({ ...prev, [proofId]: outcome }));
    };

    fetchRulings();
  }, [currentProofId, enabled, attempt]);

  const retry = useCallback(() => {
    if (!currentProofId) return;
    requestedRef.current.delete(currentProofId);
    setAttempt((previous) => previous + 1);
  }, [currentProofId]);

  const applyEntry = useCallback((proofId: string, entry: IListCheckEntry) => {
    setResults((prev) => {
      const cached = prev[proofId];
      if (!cached || cached.status !== 'success' || !cached.data) return prev;
      const rulings = cached.data.rulings.map((existing) =>
        existing.check_key !== entry.check_key
          ? existing
          : entry.source === 'analyst' && entry.candidates.length === 0
          ? { ...entry, candidates: existing.candidates }
          : entry
      );
      return { ...prev, [proofId]: { ...cached, data: { ...cached.data, rulings } } };
    });
  }, []);

  const current = (currentProofId && results[currentProofId]) || IDLE;

  return { ...current, retry, applyEntry };
};
